import DOMPurify from "dompurify";

/**
 * Wrapper around DOMPurify tuned for our own content surfaces.
 *
 * Two profiles:
 *   - "rich"  (default): allows the tags employers/admins actually type in
 *                        the job-description WYSIWYG and email templates
 *                        (headings, lists, links, bold/italic, code blocks).
 *   - "strict": pure inline formatting only. Use when the source is even
 *               less trusted or we don't want block layout leaking.
 *
 * Both profiles strip <script>, <iframe>, on* handlers, javascript: URLs
 * and <style>, which is the actual attack surface we care about here.
 *
 * Usage:
 *     <div dangerouslySetInnerHTML={{ __html: safeHtml(job.description) }} />
 */
const RICH_ALLOWED_TAGS = [
    "a", "b", "blockquote", "br", "code", "div", "em", "h1", "h2", "h3",
    "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p", "pre", "s", "small",
    "span", "strong", "sub", "sup", "table", "tbody", "td", "th", "thead",
    "tr", "u", "ul",
];
const RICH_ALLOWED_ATTR = ["href", "target", "rel", "src", "alt", "title", "class"];

const STRICT_ALLOWED_TAGS = ["b", "br", "em", "i", "p", "span", "strong", "u"];
const STRICT_ALLOWED_ATTR = ["class"];

export function safeHtml(html, profile = "rich") {
    if (html == null || html === "") return "";
    const opts = profile === "strict"
        ? { ALLOWED_TAGS: STRICT_ALLOWED_TAGS, ALLOWED_ATTR: STRICT_ALLOWED_ATTR }
        : { ALLOWED_TAGS: RICH_ALLOWED_TAGS, ALLOWED_ATTR: RICH_ALLOWED_ATTR };
    // Force target=_blank links to be safe (opener-less, no referrer leak).
    // Also enforce https-ish protocols only — no javascript:/data: URIs.
    return DOMPurify.sanitize(String(html), {
        ...opts,
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta"],
        FORBID_ATTR: ["style", "onerror", "onclick", "onload", "onmouseover", "onfocus"],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
    });
}
